import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { trackEvent, EventType } from "@/shared/utils/core";
import { PersonContact } from "@/shared/types/estateContact";

interface InviteConfirmationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  contact: PersonContact | null;
  onConfirm: () => void;
  isInviting?: boolean;
  deceasedUserData?: {
    firstName?: string;
    displayName?: string;
    surName?: string;
  } | null;
}

const InviteConfirmationDialog: React.FC<InviteConfirmationDialogProps> = ({
  isOpen,
  onOpenChange,
  contact,
  onConfirm,
  isInviting = false,
  deceasedUserData,
}) => {
  const [isAuthorized, setIsAuthorized] = React.useState(false);
  const hasConfirmedRef = React.useRef(false);

  React.useEffect(() => {
    if (isOpen) {
      setIsAuthorized(false);
      hasConfirmedRef.current = false;
      trackEvent(EventType.INVITE_MODAL_OPENED, {
        contact_has_email: !!contact?.email,
        contact_relationship: contact?.relationship || "unknown",
      });
    }
  }, [isOpen, contact]);

  const formatName = (
    firstName: string | null,
    lastName: string | null,
    middleName?: string | null,
  ): string => {
    if (!firstName && !lastName) return "Unknown Contact";
    return [firstName, middleName, lastName].filter(Boolean).join(" ");
  };

  const handleConfirm = () => {
    if (hasConfirmedRef.current) return;
    hasConfirmedRef.current = true;
    onConfirm();
  };

  if (!contact) return null;

  const contactName = formatName(contact.first_name, contact.last_name, contact.middle_name);
  const deceasedName =
    formatName(deceasedUserData?.displayName || null, deceasedUserData?.surName || null) ||
    "the deceased";

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Invite {contactName} to Elayne?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm leading-relaxed text-secondary mb-4">
            Inviting {contactName} will give them full access to {deceasedName}'s documents and
            information. Please only continue if you're comfortable sharing this level of access.
          </p>
          <div className="flex items-start space-x-2">
            <div className="pt-0.5">
              <Checkbox
                id="authorize"
                checked={isAuthorized}
                onCheckedChange={(checked) => setIsAuthorized(checked as boolean)}
              />
            </div>
            <label htmlFor="authorize" className="text-sm text-secondary cursor-pointer">
              I authorize {contactName} to be invited to {deceasedName}'s profile
            </label>
          </div>
        </DialogBody>
        <DialogFooter
          onSubmit={handleConfirm}
          submitText={isInviting ? "Inviting..." : "Invite"}
          disabled={!isAuthorized || isInviting}
          loading={isInviting}
        />
      </DialogContent>
    </Dialog>
  );
};

export default InviteConfirmationDialog;
